import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { PlayerRuntime } from '@ghostscreen/player-core';
import { createKioskRuntime } from '../lib/create-kiosk-runtime.js';
import { formatPlayerError } from '../lib/format-player-error.js';
import { isPairingAutoRefreshError } from '../lib/isPairingAutoRefreshError.js';
import type { IdleDisplay } from '../lib/idle-display.js';
import { msUntilPairingRotate } from '../lib/msUntilPairingRotate.js';
import { isValidPairingCode, normalizePairingCode } from '../lib/pairing-code.js';
import {
  exitKioskFullscreen,
  lockKioskFullscreen,
  requestWakeLock,
} from '../lib/kiosk-chrome.js';

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return error instanceof Error && error.name === 'AbortError';
}

export type KioskPhase = 'booting' | 'pairing' | 'playing';

export type KioskSession = {
  phase: KioskPhase;
  error: string | null;
  pairing: boolean;
  confirming: boolean;
  connected: boolean;
  connectedScreenName: string | null;
  sessionCode: string | null;
  sessionExpiresAt: string | null;
  sessionError: string | null;
  deviceCode: string | null;
  idleDisplay: IdleDisplay;
  stageRef: RefObject<HTMLDivElement>;
  shellRef: RefObject<HTMLDivElement>;
  submitPairing: (rawCode: string) => Promise<void>;
  refreshPairingSession: () => void;
  clearError: () => void;
};

function shellElement(shellRef: RefObject<HTMLDivElement>): HTMLElement {
  return shellRef.current ?? document.documentElement;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function useKioskSession(): KioskSession {
  const stageRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<PlayerRuntime | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const fullscreenUnlockRef = useRef<(() => void) | null>(null);
  const sessionAbortRef = useRef<AbortController | null>(null);
  const sessionGenerationRef = useRef(0);

  const [phase, setPhase] = useState<KioskPhase>('booting');
  const [error, setError] = useState<string | null>(null);
  const [pairing, setPairing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectedScreenName, setConnectedScreenName] = useState<string | null>(null);
  const [sessionCode, setSessionCode] = useState<string | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [deviceCode, setDeviceCode] = useState<string | null>(null);
  const [idleDisplay, setIdleDisplay] = useState<IdleDisplay>('logo');
  const [sessionTick, setSessionTick] = useState(0);

  const stopPlayingChrome = useCallback(() => {
    fullscreenUnlockRef.current?.();
    fullscreenUnlockRef.current = null;
    void exitKioskFullscreen();
    void wakeLockRef.current?.release();
    wakeLockRef.current = null;
  }, []);

  const startPlayingChrome = useCallback(async () => {
    stopPlayingChrome();
    fullscreenUnlockRef.current = lockKioskFullscreen(shellElement(shellRef));
    wakeLockRef.current = await requestWakeLock();
  }, [stopPlayingChrome]);

  const stopSessionWait = useCallback(() => {
    sessionAbortRef.current?.abort();
    sessionAbortRef.current = null;
  }, []);

  const finishPairing = useCallback(
    async (runtime: PlayerRuntime) => {
      setConfirming(true);
      setConnectedScreenName('Your screen');
      setConnected(true);
      await delay(1400);
      await runtime.start();
      setPhase('playing');
      setConnected(false);
      setConfirming(false);
      await startPlayingChrome();
    },
    [startPlayingChrome],
  );

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let cancelled = false;
    const runtime = createKioskRuntime(stage, {
      onRevoked: () => {
        if (cancelled) return;
        stopPlayingChrome();
        setConnected(false);
        setConfirming(false);
        setPhase('pairing');
        setError(null);
        setSessionError('This screen was disconnected. Scan again to reconnect.');
        setSessionTick((value) => value + 1);
      },
      onIdleDisplay: (next) => {
        if (!cancelled) setIdleDisplay(next);
      },
    });
    runtimeRef.current = runtime;

    void (async () => {
      try {
        const paired = await runtime.isPaired();
        if (cancelled) return;
        if (paired) {
          await runtime.start();
          if (cancelled) return;
          setPhase('playing');
          await startPlayingChrome();
        } else {
          setPhase('pairing');
        }
      } catch (err) {
        if (cancelled) return;
        setError(formatPlayerError(err));
        setPhase('pairing');
      }
    })();

    return () => {
      cancelled = true;
      stopSessionWait();
      void runtime.stop();
      runtimeRef.current = null;
      stopPlayingChrome();
    };
  }, [startPlayingChrome, stopPlayingChrome, stopSessionWait]);

  useEffect(() => {
    if (phase !== 'pairing') {
      stopSessionWait();
      return;
    }

    const runtime = runtimeRef.current;
    if (!runtime) return;

    const generation = ++sessionGenerationRef.current;
    const sessionAbort = new AbortController();
    sessionAbortRef.current?.abort();
    sessionAbortRef.current = sessionAbort;

    // First paint only — later rotates keep showing the old code until the new one is ready.
    setSessionError(null);
    setConnected(false);
    setConfirming(false);

    void (async () => {
      let softRetries = 0;
      let hasLiveCode = false;

      while (!sessionAbort.signal.aborted && generation === sessionGenerationRef.current) {
        try {
          setSessionError(null);
          setConfirming(false);
          setConnected(false);
          if (!hasLiveCode) {
            setSessionCode(null);
            setSessionExpiresAt(null);
          }

          const created = await runtime.beginPairingSession();
          if (sessionAbort.signal.aborted || generation !== sessionGenerationRef.current) return;

          softRetries = 0;
          hasLiveCode = true;
          setSessionCode(created.code);
          setSessionExpiresAt(created.expiresAt);
          setDeviceCode(created.deviceCode);

          // Child abort: timer rotate or parent teardown — not a user-facing failure.
          const pollAbort = new AbortController();
          const onSessionAbort = () => pollAbort.abort();
          sessionAbort.signal.addEventListener('abort', onSessionAbort);

          const rotateMs = msUntilPairingRotate(created.expiresAt);
          const rotateTimer = window.setTimeout(() => pollAbort.abort(), rotateMs);

          try {
            await runtime.awaitPairingSession(created.code, pollAbort.signal);
            if (sessionAbort.signal.aborted || generation !== sessionGenerationRef.current) return;
            await finishPairing(runtime);
            return;
          } catch (waitErr) {
            if (sessionAbort.signal.aborted || generation !== sessionGenerationRef.current) return;

            // Timer (or parent) aborted the wait — mint the next code silently.
            if (isAbortError(waitErr)) {
              continue;
            }

            // Server already expired / used — same silent rotate (safety net).
            if (isPairingAutoRefreshError(waitErr) && softRetries < 12) {
              softRetries += 1;
              await delay(Math.min(200 + softRetries * 150, 1_500));
              continue;
            }

            throw waitErr;
          } finally {
            window.clearTimeout(rotateTimer);
            sessionAbort.signal.removeEventListener('abort', onSessionAbort);
          }
        } catch (err) {
          if (sessionAbort.signal.aborted || generation !== sessionGenerationRef.current) return;
          if (isAbortError(err)) return;

          // beginPairingSession blip — keep the last good QR while we retry.
          if (isPairingAutoRefreshError(err) && softRetries < 12) {
            softRetries += 1;
            setSessionError(null);
            await delay(Math.min(400 + softRetries * 250, 3_000));
            continue;
          }

          setSessionError(formatPlayerError(err));
          setSessionCode(null);
          setSessionExpiresAt(null);
          setConfirming(false);
          setConnected(false);
          hasLiveCode = false;
          return;
        }
      }
    })();

    return () => {
      sessionAbort.abort();
    };
  }, [phase, sessionTick, finishPairing, stopSessionWait]);

  const submitPairing = useCallback(
    async (rawCode: string) => {
      const runtime = runtimeRef.current;
      if (!runtime) return;

      const code = normalizePairingCode(rawCode);
      if (!isValidPairingCode(code)) {
        setError('Enter the pairing code from the GhostScreens dashboard.');
        return;
      }

      stopSessionWait();
      setPairing(true);
      setConfirming(true);
      setError(null);
      try {
        await runtime.pair(code);
        await finishPairing(runtime);
      } catch (err) {
        setError(formatPlayerError(err));
        setPhase('pairing');
        setConfirming(false);
        setConnected(false);
        setSessionTick((value) => value + 1);
        stopPlayingChrome();
      } finally {
        setPairing(false);
      }
    },
    [finishPairing, stopPlayingChrome, stopSessionWait],
  );

  const refreshPairingSession = useCallback(() => {
    setSessionError(null);
    setSessionTick((value) => value + 1);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    phase,
    error,
    pairing,
    confirming,
    connected,
    connectedScreenName,
    sessionCode,
    sessionExpiresAt,
    sessionError,
    deviceCode,
    idleDisplay,
    stageRef,
    shellRef,
    submitPairing,
    refreshPairingSession,
    clearError,
  };
}
